import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Chip, Autocomplete, TextField } from '@mui/material';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import { useParams } from 'react-router-dom';
import { Server } from '../../services/Server';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SMTChart from '../common/SMTChart';
import toast from 'react-hot-toast';
import { useAppContext } from '../../contexts/AppContext';
import { buildSoilingRatiosParams, cleanFormDataForSave, cleanUserPatterns, formatRainValue } from '../../utils/utils';
import { formatRange, RegionRecommendationHelp } from './RainRecommendationField';
import { useRainRecommendation } from '../../hooks/useRainRecommendation';
import { RAIN_RECOMMENDATION_UNITS, type RainRecommendationKey } from '../../constants/rainRecommendation';
import type { RecommendedRange } from '../../types';
import type { FieldSchema } from '../form-engine/types';

// Delay before a change of curves or rain parameters is saved and the combined curve regenerated.
const AUTOSAVE_DELAY_MS = 500;

const isValidRainValue = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value);

type RainValue = number | '' | null | undefined;

interface SoilingRatioCurve {
  name?: string;
  file_uid?: string;
  type?: string;
  derived_rain_threshold?: number | null;
  derived_rain_efficiency?: number | null;
}

interface CurveOption {
  value: string;
  label: string;
  file_uid?: string;
  type: string;
}

interface CombinedSoilingRatio {
  times: string[];
  values: (number | null)[];
  rain: (number | null)[];
  rain_threshold: number;
}

const stateKey = (curves: CurveOption[], threshold: RainValue, efficiency: RainValue) =>
  JSON.stringify({
    curves: curves.map((curve) => `${curve.file_uid}|${curve.value}`).sort(),
    threshold,
    efficiency,
  });

const parseRainInput = (text: string): number | '' => {
  const parsed = Number(text);
  return text === '' || Number.isNaN(parsed) ? '' : parsed;
};

interface RainSensorSuggestion {
  label: string;
  value: number | null;
}

interface RainParameterInputProps {
  label: string;
  recommendationKey: RainRecommendationKey;
  recommendation: RecommendedRange | null | undefined;
  value: RainValue;
  onChange: (value: number | '') => void;
  step: string;
  sensorSuggestions: RainSensorSuggestion[];
}

const RainParameterInput = ({
  label,
  recommendationKey,
  recommendation,
  value,
  onChange,
  step,
  sensorSuggestions,
}: RainParameterInputProps) => (
  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
    <TextField
      type="number"
      label={label}
      value={value ?? ''}
      onChange={(event) => onChange(parseRainInput(event.target.value))}
      inputProps={{ step }}
      error={!isValidRainValue(value)}
      sx={{ width: 280 }}
    />
    <Box>
      {recommendation && (
        <Typography variant="body2" color="text.secondary">
          Region recommendation: <b>{formatRange(recommendation, recommendationKey)}</b>
          <RegionRecommendationHelp />
        </Typography>
      )}
      {sensorSuggestions.map((suggestion) => (
        <Typography key={suggestion.label} variant="body2" color="text.secondary">
          Value derived from {suggestion.label}:{' '}
          <b>
            {suggestion.value === null
              ? 'not available (no cleaning events)'
              : `${formatRainValue(suggestion.value)}${
                  RAIN_RECOMMENDATION_UNITS[recommendationKey] ? ` ${RAIN_RECOMMENDATION_UNITS[recommendationKey]}` : ''
                }`}
          </b>
        </Typography>
      ))}
    </Box>
  </Box>
);

interface SoilingRatioMultiSelectProps {
  field: FieldSchema;
}

const SoilingRatioMultiSelect: React.FC<SoilingRatioMultiSelectProps> = () => {
  const { setFormData, getFormDataItem } = useFormContext();
  const formData = useFormData();
  const { role } = useAppContext();
  const params = useParams();
  const { URL_BASE, postToServerWithParams, putProjectAPI } = Server();
  const queryClient = useQueryClient();
  const [selectedCurves, setSelectedCurves] = useState<CurveOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  const [combinedChartData, setCombinedChartData] = useState<CombinedSoilingRatio | null>(null);
  const combinedCurves = useMemo(
    () =>
      combinedChartData && [
        {
          name: 'Combined soiling ratio',
          times: combinedChartData.times,
          values: combinedChartData.values,
          rain: combinedChartData.rain,
          curve_rain_threshold: combinedChartData.rain_threshold,
        },
      ],
    [combinedChartData],
  );
  const hasRestoredDataRef = useRef(false);
  const latestRequestRef = useRef(0);
  // Selection and rain parameters as last saved to the project; compared against the current state to decide whether to save.
  const persistedStateRef = useRef<string | null>(null);
  const { data: rainRecommendation } = useRainRecommendation();

  // Helper function to get the correct soiling ratio operational key based on role
  const soilingRatioOperationalKey = role === 'ex_post_analysis' ? 'soiling_ratio_operational_ex_post' : 'soiling_ratio_operational';
  const operationalData = formData?.[soilingRatioOperationalKey] || {};

  // Saved rain parameters. Empty until the climate recommendation fills them in (see the Project page).
  const thresholdRecommendation = rainRecommendation?.rain_threshold ?? null;
  const efficiencyRecommendation = rainRecommendation?.rain_efficiency ?? null;
  const rainThreshold: number | '' | null = operationalData.rain_threshold ?? null;
  const rainEfficiency: number | '' | null = operationalData.rain_efficiency ?? null;

  // Memoize sensor data to avoid re-renders - use JSON.stringify to create stable reference
  const sensorDataString = useMemo(() => {
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));
    const data: { [key: string]: { file_uid: string; name: string } } = {};
    soilingDataKeys.forEach((blockKey) => {
      const fileUid = getFormDataItem<string>(formData, `${blockKey}.file_uid`);
      const name = getFormDataItem<string>(formData, `${blockKey}.name`);
      if (fileUid) {
        data[fileUid] = { file_uid: fileUid, name: name || '' };
      }
    });
    return JSON.stringify(data);
  }, [formData, getFormDataItem]);

  const sensorData = useMemo(() => JSON.parse(sensorDataString), [sensorDataString]);

  // Function to get sensor name from file_uid
  const getSensorName = useCallback(
    (fileUid: string | undefined) => {
      if (!fileUid) return '';
      const sensor = sensorData[fileUid];
      return sensor?.name || `Sensor ${fileUid.substring(0, 8)}...`;
    },
    [sensorData],
  );

  // Only params are needed: the backend reads everything (sensor files + context) from the project.
  const requestParams = buildSoilingRatiosParams(params.id, role);

  // Fetch data from SMTChart cache (same cache key)
  const {
    data: soilingRatioData = [],
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    isError: isDataError,
  } = useQuery({
    queryKey: ['smt-chart-' + 'soiling-ratios'],
    queryFn: () => postToServerWithParams<SoilingRatioCurve[]>(`${URL_BASE}soiling-ratios`, requestParams, {}),
    enabled: !!(
      formData?.location?.latitude &&
      formData?.location?.longitude &&
      !isNaN(Number(formData.location.latitude)) &&
      !isNaN(Number(formData.location.longitude))
    ),
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 0, // Consider data as stale immediately
  });

  // Calculate available curves directly (without useEffect to avoid loops)
  const availableCurves = useMemo<CurveOption[]>(() => {
    if (!soilingRatioData || !Array.isArray(soilingRatioData)) {
      return [];
    }

    return soilingRatioData
      .filter((curve: SoilingRatioCurve) => curve.name !== 'COMBINED' && (role === 'ex_post_analysis' ? curve.name !== 'hsu' : true)) // Exclude only the COMBINED curve
      .map((curve: SoilingRatioCurve, index: number) => {
        // Get sensor name and format the curve label
        const sensorName = curve.file_uid ? getSensorName(curve.file_uid) : '';
        const curveName = curve.name || `Curve ${index + 1}`;
        const displayLabel = sensorName ? `${sensorName} - ${curveName}` : curveName;

        return {
          value: curveName, // Keep original curve name as value for API calls
          label: displayLabel, // Display name with sensor name
          file_uid: curve.file_uid,
          type: curve.type || 'rde300',
        };
      });
  }, [soilingRatioData, getSensorName]);

  // Load existing combined data and selected curves from formData on component mount
  useEffect(() => {
    // Only restore once and only when we have available curves
    if (hasRestoredDataRef.current || availableCurves.length === 0) {
      return;
    }

    // Restore selected curves based on sensor_files
    const restoredCurves: CurveOption[] = [];
    if (operationalData.sensor_files && Array.isArray(operationalData.sensor_files)) {
      operationalData.sensor_files.forEach((sensorFile: { uid: string; selected?: string[] }) => {
        if (sensorFile.selected && Array.isArray(sensorFile.selected)) {
          sensorFile.selected.forEach((curveName: string) => {
            const matchingCurve = availableCurves.find((curve) => curve.file_uid === sensorFile.uid && curve.value === curveName);
            if (matchingCurve) {
              restoredCurves.push(matchingCurve);
            }
          });
        }
      });

      // Handle HSU selection persistence separately
      if (operationalData.hsu_selected) {
        const hsuCurve = availableCurves.find(
          (curve) => curve.value?.toLowerCase() === 'hsu' || curve.label?.toLowerCase().includes('hsu'),
        );
        if (hsuCurve && !restoredCurves.some((c) => c.value === hsuCurve.value && c.file_uid === hsuCurve.file_uid)) {
          restoredCurves.push(hsuCurve);
        }
      }

      if (restoredCurves.length > 0) {
        setSelectedCurves(restoredCurves);
      }
    }

    // Mark as restored after processing (moved outside the conditional)
    persistedStateRef.current = stateKey(restoredCurves, operationalData.rain_threshold, operationalData.rain_efficiency);
    hasRestoredDataRef.current = true;
    setIsRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCurves]);

  const handleCurveSelectionChange = (_event: unknown, newValue: CurveOption[]) => {
    setSelectedCurves(newValue);
  };

  const buildSensorFiles = (curves: CurveOption[]) => {
    const sensorFiles: { uid: string; type: string; selected: string[]; name: string }[] = [];
    Object.keys(formData)
      .filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'))
      .forEach((blockKey) => {
        const fileUid = getFormDataItem<string>(formData, `${blockKey}.file_uid`);
        if (fileUid) {
          sensorFiles.push({
            uid: fileUid,
            type: getFormDataItem<string>(formData, `${blockKey}.type`) || 'rde300',
            selected: curves.filter((curve) => curve.file_uid === fileUid).map((curve) => curve.value),
            name: getFormDataItem<string>(formData, `${blockKey}.name`) || `Sensor ${fileUid.substring(0, 8)}...`,
          });
        }
      });
    return sensorFiles;
  };

  const isHsuSelected = (curves: CurveOption[]) =>
    curves.some((curve) => curve.value.toLowerCase().includes('hsu') || curve.label.toLowerCase().includes('hsu'));

  const updateRainParameter = (key: 'rain_threshold' | 'rain_efficiency', value: number | '') => {
    setFormData({ ...formData, [soilingRatioOperationalKey]: { ...operationalData, [key]: value } });
  };

  // Saves the selection and rain parameters (when requested), then computes the combined curve from the persisted project.
  // The combined curve is not stored in the project; it is recomputed whenever it is needed.
  const updateCombinedCurve = async (curves: CurveOption[], threshold: number, efficiency: number, shouldSave: boolean) => {
    const requestId = ++latestRequestRef.current;
    const isLatestRequest = () => requestId === latestRequestRef.current;
    setIsLoading(true);
    try {
      if (shouldSave) {
        const selectionFormData = {
          ...formData,
          [soilingRatioOperationalKey]: {
            ...operationalData,
            sensor_files: buildSensorFiles(curves),
            hsu_selected: isHsuSelected(curves),
            rain_threshold: threshold,
            rain_efficiency: efficiency,
          },
        };
        setFormData(selectionFormData);
        // The backend reads the selection and rain parameters from the persisted project, so save them first.
        await putProjectAPI(params.id as string, cleanUserPatterns(cleanFormDataForSave(selectionFormData)));
        // Refresh the cached project: the form is rebuilt from it on process switch, so a stale copy would revert the save.
        queryClient.invalidateQueries({ queryKey: ['project', params.id] });
        persistedStateRef.current = stateKey(curves, threshold, efficiency);
      }

      if (curves.length === 0) {
        if (isLatestRequest()) {
          setCombinedChartData(null);
        }
        return;
      }

      // Only project_id and the context (ex-post vs operational) are sent; everything else is read from the persisted project.
      const result = await postToServerWithParams<CombinedSoilingRatio>(
        `${URL_BASE}combine-soiling-ratios`,
        { project_id: params.id, ex_post: role === 'ex_post_analysis' },
        {},
      );
      if (isLatestRequest() && result?.times && result?.values) {
        setCombinedChartData(result);
      }
    } catch (error) {
      console.error('Error combining curves:', error);
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while combining curves. Please try again.';
      toast.error(errorMessage);
    } finally {
      if (isLatestRequest()) {
        setIsLoading(false);
      }
    }
  };

  // Save and regenerate the combined curve whenever the selection or a rain parameter changes (debounced).
  // Compared with the last persisted state, not with formData, which is updated before each save.
  useEffect(() => {
    if (!isRestored || !isValidRainValue(rainThreshold) || !isValidRainValue(rainEfficiency)) {
      return;
    }
    const shouldSave = stateKey(selectedCurves, rainThreshold, rainEfficiency) !== persistedStateRef.current;
    const timer = setTimeout(() => {
      updateCombinedCurve(selectedCurves, rainThreshold, rainEfficiency, shouldSave);
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestored, selectedCurves, rainThreshold, rainEfficiency]);

  const thresholdSuggestions = useMemo<RainSensorSuggestion[]>(
    () =>
      (Array.isArray(soilingRatioData) ? soilingRatioData : [])
        .filter((curve) => curve.file_uid)
        .map((curve) => ({ label: getSensorName(curve.file_uid), value: curve.derived_rain_threshold ?? null })),
    [soilingRatioData, getSensorName],
  );

  const efficiencySuggestions = useMemo<RainSensorSuggestion[]>(
    () =>
      (Array.isArray(soilingRatioData) ? soilingRatioData : [])
        .filter((curve) => curve.file_uid)
        .map((curve) => ({ label: getSensorName(curve.file_uid), value: curve.derived_rain_efficiency ?? null })),
    [soilingRatioData, getSensorName],
  );

  if (isDataLoading || isDataFetching) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {isDataLoading ? 'Loading soiling ratio data...' : 'Refreshing soiling ratio data...'}
        </Typography>
      </Box>
    );
  }

  if (isDataError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          Error loading soiling ratio data. Please check your configuration.
        </Typography>
      </Box>
    );
  }

  if (availableCurves.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No curves available. Please ensure:
          <br />• Location data is configured
          <br />• Soiling ratio data is loaded
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Autocomplete
        multiple
        options={availableCurves}
        value={selectedCurves}
        onChange={handleCurveSelectionChange}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value && option.file_uid === value.file_uid}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Curves to Combine"
            placeholder="Choose curves..."
            helperText="Select multiple curves to combine them"
            fullWidth
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.value} label={option.label} color="primary" variant="outlined" />
          ))
        }
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {option.label}
                </Typography>
              </Box>
            </Box>
          );
        }}
        sx={{ mb: 3, width: '50%' }}
      />

      <RainParameterInput
        label="Rain threshold"
        recommendationKey="rain_threshold"
        recommendation={thresholdRecommendation}
        value={rainThreshold}
        onChange={(value) => updateRainParameter('rain_threshold', value)}
        step="0.1"
        sensorSuggestions={thresholdSuggestions}
      />
      <RainParameterInput
        label="Rain efficiency"
        recommendationKey="rain_efficiency"
        recommendation={efficiencyRecommendation}
        value={rainEfficiency}
        onChange={(value) => updateRainParameter('rain_efficiency', value)}
        step="0.01"
        sensorSuggestions={efficiencySuggestions}
      />

      {selectedCurves.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Please select at least one curve to combine.
        </Typography>
      )}

      {isLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Updating combined curve...
        </Typography>
      )}

      {combinedCurves ? (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
            Combined Soiling Ratio
          </Typography>
          <SMTChart
            getDataFromApi={false}
            data={combinedCurves}
            url=""
            method="get"
            params={undefined}
            body={{}}
            properties={{ ymin: 0.4, ymax: 1.2, ylabel: 'Soiling Ratio', show_rain_threshold: true }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default SoilingRatioMultiSelect;
